import {Icon} from '@ezcater/tapas';
import {faClockRotateLeft} from '@fortawesome/pro-regular-svg-icons';
import useTranslation from 'next-translate/useTranslation';
import {twMerge} from 'tailwind-merge';

import {type EventBarSearchHistoryEntry} from './hooks/useEventBarSearchHistory';

type EventBarSearchHistoryOptionProps = {
  entry: EventBarSearchHistoryEntry;
  id: string;
  isActive?: boolean;
  onSelect: () => void;
  // When false, only the address is shown (no When / Who summary) — used by the Where panel.
  showSummary?: boolean;
};

// A past search shown in the What / Where panels. The street is the
// bold primary text (falling back to the City, ST label) and a second line carries the location
// (City, ST) plus the When / Who summary (date · time · guests). Clicking re-applies the fields.
const EventBarSearchHistoryOption: React.FC<EventBarSearchHistoryOptionProps> = ({
  entry,
  id,
  isActive = false,
  onSelect,
  showSummary = true,
}) => {
  const {t} = useTranslation('app-bar');
  const {addressLabel, address, dateLabel, timeLabel, headcount} = entry;

  // Prefer the street as the title (falling back to the City, ST label). With the summary, City, ST
  // moves into the subtitle; without it (e.g. Where) the city is dropped rather than shown below.
  const title = address.street || addressLabel;

  const subtitle = showSummary
    ? [
        address.street ? addressLabel : null,
        dateLabel,
        timeLabel,
        headcount != null ? t('eventBarV2.what.guests', {count: headcount}) : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  return (
    <button
      aria-selected={isActive}
      className={twMerge(
        'flex w-full cursor-pointer items-center gap-4 rounded-lg px-2 py-2 text-left hover:bg-peppercorn-50',
        isActive && 'bg-peppercorn-50',
      )}
      data-testid="event-bar-search-history-option"
      id={id}
      onClick={onSelect}
      onMouseDown={event => event.preventDefault()}
      role="option"
      tabIndex={-1}
      type="button"
    >
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-peppercorn-50 text-peppercorn-600">
        <Icon aria-hidden icon={faClockRotateLeft} size="small" />
      </span>
      <span className="min-w-0 overflow-hidden">
        <span className="block truncate font-semibold text-peppercorn-800">{title}</span>
        {subtitle && <span className="block truncate text-sm text-peppercorn-800">{subtitle}</span>}
      </span>
    </button>
  );
};

export default EventBarSearchHistoryOption;
